// Vault Interest (docs/VAULT_INTEREST_SCOPE.md) — "Money Monday".
//
// The model, as signed off (Alan, Aug 2026): the parent sets a YEARLY rate (the language
// parents already think in; a %-per-week model was rejected as APR-explosive, 5%/wk ≈
// 1,162% APR). An earlier version of this comment claimed "pocket-money parents average
// ~9%/yr" and the rate picker repeated it to parents. It had no source — nothing measures a
// pocket-money interest rate — so it was removed on 13 Sep. Do not reintroduce a benchmark
// here without one; LB-276 is the honest version, a median over households actually using
// this feature. It's paid in 52 weekly instalments —
// yearly rate ÷ 52 — every Monday ("Money Monday", the kid-facing name), on whatever's in
// the Savings Vault at that point. Interest is paid INTO the vault, so it compounds with
// no separate compounding logic. Vault-only, deliberately: tokens outside the vault earn
// nothing — that contrast IS the lesson.
//
// Accrual is LAZY — there is no cron. applyVaultInterest() runs whenever household state
// is (re)loaded (Root.jsx: initial load, focus/visibility refetch, join) and pays every
// full Monday that has passed since each child's anchor, one transaction row per week, so
// the ledger visibly shows the compounding step-by-step even after a fortnight away.
// It's a pure function of (anchor, balance, rate, carry), so two devices racing the same
// catch-up compute identical balances — whichever save wins, no double-pay is possible,
// same last-write-wins posture as every other write in this app.
//
// Rounding: sub-penny fractions carry over week to week on the profile
// (vaultInterestCarry, fractional pence) so nobody ever loses a crumb — with one floor:
// any vault holding £1 or more is guaranteed at least 1p every Money Monday, so the
// weekly ritual never pays zero (worst-case cost of the floor: ~52p/child/year). Below
// £1, fractions simply accumulate until they make a whole penny.
//
// Deliberately near-dependency-free — no imports from utils.js/constants.js (utils pulls
// in Common.jsx and therefore JSX). That keeps this module importable from plain Node,
// which is exactly how tests/specs/21-vault-interest.spec.js unit-tests the maths without
// a browser. The tiny date helpers below mirror utils.js's local-date conventions
// (todayStr / weekStartOf / dateStrAddDays) byte-for-byte in behaviour; if those ever
// change, change these to match.

export const DEFAULT_VAULT_INTEREST_APR = 10;
export const VAULT_INTEREST_APR_PRESETS = [5, 7, 10, 25];
// P3 (workplan, approved 27 Aug): HARD CAP 25%. The point of vault interest is "saving
// pays", not "numbers go brrr" — above ~25% the weekly payouts dwarf earned tokens and
// teach the wrong lesson entirely. clampApr() already runs at every accrual, so lowering
// this cap automatically caps the EFFECTIVE rate for any household that slipped above
// it while the input allowed 100; migrateState clamps the stored value too so displays
// and the settings card agree with what actually accrues.
export const MAX_VAULT_INTEREST_APR = 25;

const pad2 = (n) => String(n).padStart(2, "0");

// Mirrors utils.js todayStr(): LOCAL yyyy-mm-dd.
export function viTodayStr(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// Mirrors utils.js dateStrAddDays().
export function viDateStrAddDays(dateStr, delta) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// Mirrors utils.js weekStartOf(): the Monday of the week containing dateStr.
export function viWeekStartOf(dateStr) {
  const day = new Date(dateStr + "T00:00:00").getDay(); // 0=Sun..6=Sat
  return viDateStrAddDays(dateStr, day === 0 ? -6 : 1 - day);
}

// Whether this child earns vault interest at all. Local copy (rather than an import) for the
// same no-JSX-import reason as above.
//
// (Alan, Aug 2026) This replaces viBankEnabled(), which read the old hasBankAccount flag. The
// Savings Vault itself is no longer optional, so vault-enabled is not a question any more —
// but it had been doubling as the per-child interest opt-out, and that capability had to
// survive. migrateState derives earnsVaultInterest from the old flag, so a child a parent had
// deliberately excluded stays excluded.
//
// Missing field reads as ON, matching the migration default and staying safe against a
// profile object that skipped migration (e.g. preview-mode seeds).
function earnsInterest(child) {
  return child?.earnsVaultInterest !== false;
}

// Clamp a stored/entered rate to something sane. Non-numeric falls back to the default.
export function clampApr(apr) {
  if (typeof apr !== "number" || !isFinite(apr) || apr <= 0) return DEFAULT_VAULT_INTEREST_APR;
  return Math.min(MAX_VAULT_INTEREST_APR, Math.max(1, Math.round(apr)));
}

// The exact (fractional-pence) value of one week's interest on a vault balance.
export function weeklyInterestExactPence(vaultPence, apr) {
  return ((vaultPence || 0) * clampApr(apr)) / 100 / 52;
}

// What next Money Monday will actually pay this child, given their current carry —
// floor of (exact + carry), with the ≥£1 minimum-1p guarantee. Used by every preview
// surface (parent cost preview, kid vault card, hero line) so the number shown is the
// number paid.
export function nextMondayPayoutPence(child, apr) {
  const balance = child?.vaultBalancePence || 0;
  const carry = typeof child?.vaultInterestCarry === "number" ? child.vaultInterestCarry : 0;
  const exact = weeklyInterestExactPence(balance, apr) + carry;
  let payout = Math.floor(exact);
  if (payout < 1 && balance >= 100) payout = 1;
  return payout;
}

// LB-385 (30 Sep): how many days until this child's next Money Monday, for the kid vault
// card's chip. 0 means today: a Monday whose interest has not been settled yet (accrual is
// lazy, so it pays on the next load, see applyVaultInterest). Otherwise 1 to 7 — a Monday
// already settled reads as next Monday, 7 days away.
export function daysToMoneyMonday(child, today = viTodayStr()) {
  const dow = new Date(today + "T00:00:00").getDay(); // 0=Sun..6=Sat
  if (dow === 1) return child?.vaultInterestAnchor === viWeekStartOf(today) ? 7 : 0;
  return (8 - dow) % 7;
}

// Rough full-year cost of a child's vault at the given rate — parent-facing honesty line.
export function yearlyInterestPence(vaultPence, apr) {
  return Math.round(((vaultPence || 0) * clampApr(apr)) / 100);
}

// Lifetime "Earned so far" for a child, summed straight from the ledger (interest-tagged
// credits, reversals excluded) — zero extra stored state, always consistent with history.
export function interestEarnedPence(transactions, childId) {
  return (transactions || [])
    .filter((t) => t.childId === childId && t.interest && t.type === "credit" && !t.reversed && !t.reversalOf)
    .reduce((sum, t) => sum + (t.amount || 0), 0);
}

let uidCounter = 0;
function viUid() {
  // Matches the shape/purpose of constants.js uid() without importing it (see header).
  return `vi_${Date.now().toString(36)}_${(uidCounter++).toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// The lazy accrual pass. MUTATES `state` in place (same convention as migrateState) and
// returns true if anything changed — balances, transactions, anchors, or carries.
//
// Rules:
//  - Disabled => complete no-op (anchors untouched; enabling RESETS anchors to the current
//    week — see VaultInterestCard's toggle — so a period spent off is never back-paid).
//  - Vault switched off for a child => no interest accrues for them; their anchor
//    fast-forwards so re-enabling the vault never back-pays the off period either.
//  - One `interest: true` credit transaction per child per missed Monday, timestamped at
//    the Monday it covers (07:00 local), reason carrying the rate — the auditable ledger.
//  - Compounds naturally: each week's payout lands in vaultBalancePence before the next
//    week's calculation reads it.
export function applyVaultInterest(state, today = viTodayStr(), uid = viUid) {
  const vi = state?.settings?.vaultInterest;
  if (!vi || !vi.enabled) return false;
  const apr = clampApr(vi.apr);
  const currentWeek = viWeekStartOf(today);
  let changed = false;

  (state.profiles || []).forEach((p) => {
    let anchor = typeof p.vaultInterestAnchor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(p.vaultInterestAnchor)
      ? p.vaultInterestAnchor
      : null;

    if (!anchor) {
      // First time interest has ever looked at this child (e.g. a profile added after
      // enabling) — start the clock now, pay nothing retroactively.
      p.vaultInterestAnchor = currentWeek;
      if (typeof p.vaultInterestCarry !== "number") p.vaultInterestCarry = 0;
      changed = true;
      return;
    }

    if (!earnsInterest(p)) {
      // Opted out — no accrual, and no back-pay later: skip the elapsed weeks entirely.
      //
      // DO NOT "optimise" this away (Alan, Aug 2026). It looks like a cheap early-exit that
      // could be hoisted into a filter before the forEach, or skipped for ineligible
      // children — it can't. This branch running on EVERY load is precisely what keeps an
      // opted-out child's anchor marching forward with the current week. Stop advancing it
      // and their anchor freezes at the moment they were switched off, so the day a parent
      // opts them back in, the accrual loop below sees every intervening week as unpaid and
      // settles the lot in one payment. This is the one place in the module where a
      // reasonable-looking refactor silently pays out real money.
      if (anchor !== currentWeek) {
        p.vaultInterestAnchor = currentWeek;
        p.vaultInterestCarry = 0;
        changed = true;
      }
      return;
    }

    let carry = typeof p.vaultInterestCarry === "number" && isFinite(p.vaultInterestCarry) && p.vaultInterestCarry >= 0
      ? p.vaultInterestCarry
      : 0;
    let safety = 0;
    while (anchor < currentWeek && safety++ < 520) {
      anchor = viDateStrAddDays(anchor, 7);
      const balance = p.vaultBalancePence || 0;
      const exact = weeklyInterestExactPence(balance, apr) + carry;
      let payout = Math.floor(exact);
      if (payout < 1 && balance >= 100) {
        payout = 1; // ≥£1 minimum-1p guarantee — the ritual never pays zero.
        carry = 0;
      } else {
        carry = exact - payout;
      }
      if (payout > 0) {
        p.vaultBalancePence = balance + payout;
        state.transactions.unshift({
          id: uid(),
          deviceId: "system",
          childId: p.id,
          amount: payout,
          reason: `Vault interest (${apr}% a year)`,
          type: "credit",
          balance: "vault",
          timestamp: new Date(anchor + "T07:00:00").toISOString(),
          interest: true,
          apr,
          weekStart: anchor,
        });
        changed = true;
      }
    }
    // Kill float drift so carry never accumulates representation noise across years.
    carry = Math.round(carry * 1e6) / 1e6;
    if (p.vaultInterestAnchor !== anchor || p.vaultInterestCarry !== carry) {
      p.vaultInterestAnchor = anchor;
      p.vaultInterestCarry = carry;
      changed = true;
    }
  });

  return changed;
}

// ------------------------------- stale-vault nudge -------------------------------
// "Interest that keeps the whole family honest" (Alan, Aug 2026): if the app's vault
// balance drifts from reality, the interest bill makes the drift cost real money — a
// parent paying interest on football cards bought weeks ago is paying for their own
// stale ledger. This helper powers the Settings red dot + the in-card note: a child is
// "stale" when interest is on, their vault is on and holds ≥ £1, and no HUMAN vault
// activity (any vault-balance transaction that isn't an automatic interest payment —
// transfers, redemptions, manual adjustments, reversals all count) has been recorded for
// STALE_VAULT_DAYS. A child with no human vault activity on record at all only flags once
// interest itself has been running that long — before that, silence means "new", not
// "stale". Derived entirely from the ledger; no new stored state. Deliberately a nudge,
// never an auto-pause: punishing inactivity would undermine the exact "leave it alone and
// it grows" lesson the kids are meant to absorb.
export const STALE_VAULT_DAYS = 42; // 6 weeks

export function staleVaultChildren(state, today = viTodayStr()) {
  const vi = state?.settings?.vaultInterest;
  if (!vi || !vi.enabled) return [];
  const cutoff = new Date(viDateStrAddDays(today, -STALE_VAULT_DAYS) + "T00:00:00").getTime();
  return (state.profiles || []).filter((p) => {
    if (!earnsInterest(p) || (p.vaultBalancePence || 0) < 100) return false;
    const vaultTxns = (state.transactions || []).filter((t) => t.childId === p.id && t.balance === "vault" && t.timestamp);
    const humanTimes = vaultTxns.filter((t) => !t.interest).map((t) => new Date(t.timestamp).getTime()).filter((n) => !Number.isNaN(n));
    if (humanTimes.length > 0) return Math.max(...humanTimes) < cutoff;
    const interestTimes = vaultTxns.filter((t) => t.interest).map((t) => new Date(t.timestamp).getTime()).filter((n) => !Number.isNaN(n));
    if (interestTimes.length === 0) return false;
    return Math.min(...interestTimes) < cutoff;
  });
}
